/**
 * TanStack Query hooks — jedno místo, kde se definují query klíče a endpointy.
 *
 * Cesty i typy odpovídají skutečnému OpenAPI (`npm run gen:api`). Klíče jsou
 * strukturované (`['overview', filters]`), aby šlo invalidovat celé podstromy
 * po akcích (potvrzení mappingu, spuštění jobu).
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryOptions } from '@tanstack/react-query';
import { api } from './client';
import type {
  AiBudget,
  AiConfig,
  AiConfigAdmin,
  AiConfigUpdate,
  AiJob,
  AiRun,
  AiRunList,
  ChatMessage,
  ChatSession,
  ClickCatalog,
  ClickHeatmap,
  ClickOverview,
  Compare,
  CurrentUser,
  EtlHealth,
  Experiment,
  ExperimentCreate,
  DkSplitCreate,
  PlacementChannels,
  ExperimentDecision,
  ExperimentEnvironment,
  ExperimentEvaluation,
  ExperimentListItem,
  FraudAssessment,
  GlobalFilters,
  GuardPhases,
  GuardPlacementDetail,
  GuardRegionVariants,
  GuardCompare,
  GuardStyles,
  GuardImpOrder,
  GuardChannelDefs,
  GuardPlacements,
  GuardRiskReport,
  Insight,
  InsightCreate,
  InsightList,
  JobRun,
  KnowledgeSearchResult,
  MappingReview,
  MetricCatalog,
  MetricsQueryRequest,
  MetricsQueryResponse,
  OverviewResponse,
  PlacementCatalog,
  PositionConfig,
  PositionRegistry,
  PositionPatchRequest,
  ChangeOwnPasswordRequest,
  SetUserPasswordRequest,
  User,
  UserCreateRequest,
  UserListResponse,
  UserUpdateRequest,
  WriteResult,
  PositionChannels,
  PositionStyles,
  PositionImpOrder,
  PositionDevice,
  DevicesOverview,
  PositionEconomy,
  PositionGeo,
  PositionGeometry,
  PositionQuality,
  PositionSummary,
  PositionTime,
  PositionVariants,
  YieldTip,
} from './models';

/** Kontext stránky posílaný do chatu (aktivní placement, experiment, web…). */
export type PageContext = Record<string, unknown>;

export const queryKeys = {
  me: ['me'] as const,
  metricCatalog: ['metrics', 'catalog'] as const,
  etlHealth: ['etl', 'health'] as const,
  jobRuns: ['etl', 'runs'] as const,

  overview: (filters: Partial<GlobalFilters>) => ['overview', filters] as const,
  positionTab: (id: string, tab: string, filters?: unknown) =>
    ['position', id, tab, filters ?? null] as const,

  experiments: (state?: string) => ['experiments', state ?? 'all'] as const,
  experiment: (id: number) => ['experiment', id] as const,
  experimentEvaluation: (id: number) => ['experiment', id, 'evaluation'] as const,
  experimentEnvironment: (id: number) => ['experiment', id, 'environment'] as const,
  experimentMapping: (id: number) => ['experiment', id, 'mapping'] as const,
  experimentReport: (id: number) => ['experiment', id, 'report'] as const,
  experimentDecisions: (id: number) => ['experiment', id, 'decisions'] as const,

  debt: ['debt'] as const,
  debtFamily: (family: string) => ['debt', family] as const,
  debtFinancial: (family: string) => ['debt', family, 'financial'] as const,
  debtTimeline: (family: string) => ['debt', family, 'timeline'] as const,

  knowledgeSearch: (q: string, filters?: unknown) =>
    ['knowledge', 'search', q, filters ?? null] as const,
  insights: (filters?: unknown) => ['knowledge', 'insights', filters ?? null] as const,
  insight: (slug: string) => ['knowledge', 'insight', slug] as const,
  relatedNotes: (subject: string) => ['knowledge', 'related', subject] as const,

  aiConfig: ['ai', 'config'] as const,
  aiConfigAdmin: ['ai', 'config', 'admin'] as const,
  aiRuns: (filters?: unknown) => ['ai', 'runs', filters ?? null] as const,
  aiJobs: ['ai', 'jobs'] as const,
  aiBudget: ['ai', 'budget'] as const,
  yieldTips: ['ai', 'tips'] as const,
  chatSessions: ['ai', 'chat', 'sessions'] as const,
  chatMessages: (sessionId: number) => ['ai', 'chat', sessionId, 'messages'] as const,

  users: (params?: unknown) => ['admin', 'users', params ?? null] as const,
  positions: ['etl', 'positions'] as const,
  mcpTokens: ['admin', 'mcp-tokens'] as const,
  oauthClients: ['admin', 'oauth-clients'] as const,
};

/** Filtry → query parametry. Pole se posílají jako opakovaný klíč. */
export function filtersToParams(f: Partial<GlobalFilters>) {
  return {
    date_from: f.date_from,
    date_to: f.date_to,
    compare: f.compare && f.compare !== 'none' ? f.compare : undefined,
    domain: f.domain?.length ? f.domain : undefined,
    type: f.type?.length ? f.type : undefined,
    supplier: f.supplier?.length ? f.supplier : undefined,
    phase: f.phase?.length ? f.phase : undefined,
    channel_tag: f.channel_tag?.length ? f.channel_tag : undefined,
    placement_ids: f.placement_ids?.length ? f.placement_ids : undefined,
  };
}

// ---------------------------------------------------------------------------
// Auth + globální stav
// ---------------------------------------------------------------------------

export function useCurrentUser(options?: Partial<UseQueryOptions<CurrentUser>>) {
  return useQuery<CurrentUser>({
    queryKey: queryKeys.me,
    queryFn: () => api.get<CurrentUser>('/auth/me'),
    retry: false,
    staleTime: 5 * 60_000,
    ...options,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      api.post<{ user: CurrentUser }>('/auth/login', body),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.me, data.user);
      void qc.invalidateQueries();
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ ok: boolean }>('/auth/logout'),
    onSuccess: () => qc.clear(),
  });
}

// --- správa uživatelů (role admin) -----------------------------------------

export function useUsers(params?: { include_inactive?: boolean; q?: string }) {
  return useQuery<UserListResponse>({
    queryKey: queryKeys.users(params),
    queryFn: () => api.get<UserListResponse>('/auth/users', { params }),
    // 403 u ne-admina není chyba k opakování, je to odpověď.
    retry: false,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UserCreateRequest) => api.post<User>('/auth/users', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: UserUpdateRequest & { id: number }) =>
      api.patch<User>(`/auth/users/${id}`, body),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      // Když admin změnil roli sám sobě, musí se přepnout i vlastní session,
      // jinak by UI dál nabízelo akce, které backend odmítne.
      qc.setQueryData<CurrentUser | undefined>(queryKeys.me, (prev) =>
        prev && prev.id === updated.id ? { ...prev, ...updated } : prev,
      );
    },
  });
}

/** Admin nastaví heslo cizímu účtu. Heslo jde výhradně v těle POSTu. */
export function useSetUserPassword() {
  return useMutation({
    mutationFn: ({ id, password }: SetUserPasswordRequest & { id: number }) =>
      api.post<{ message_cs?: string }>(`/auth/users/${id}/password`, { password }),
  });
}

/** Vlastní heslo si mění každý přihlášený, i `viewer`. */
export function useChangeOwnPassword() {
  return useMutation({
    mutationFn: (body: ChangeOwnPasswordRequest) =>
      api.post<{ message_cs?: string }>('/auth/password', body),
  });
}

/** Katalog metrik a dimenzí — zdroj pro nabídky filtrů (01 FR-5). */
export function useMetricCatalog() {
  return useQuery<MetricCatalog>({
    queryKey: queryKeys.metricCatalog,
    queryFn: () => api.get<MetricCatalog>('/metrics/catalog'),
    staleTime: 30 * 60_000,
  });
}

/** Zdraví ETL — pohání indikátor čerstvosti i stránku správy. */
export function useEtlHealth() {
  return useQuery<EtlHealth>({
    queryKey: queryKeys.etlHealth,
    queryFn: () => api.get<EtlHealth>('/etl/health'),
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
  });
}

export function useJobRuns() {
  return useQuery<JobRun[]>({
    queryKey: queryKeys.jobRuns,
    queryFn: () => api.get<JobRun[]>('/etl/runs'),
    refetchInterval: 60_000,
  });
}

export function useTriggerJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (job: string) => api.post(`/etl/run/${encodeURIComponent(job)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.jobRuns });
      void qc.invalidateQueries({ queryKey: queryKeys.etlHealth });
    },
  });
}

// --- registr pozic (čtení analyst+, zápis admin) ---------------------------

/** Registr pozic — příznaky ingest/ab_test/guard a nabídka nedávno viděných. */
export function usePositions() {
  return useQuery<PositionRegistry>({
    queryKey: queryKeys.positions,
    queryFn: () => api.get<PositionRegistry>('/etl/positions'),
    // 403 u ne-analytika není chyba k opakování, je to odpověď.
    retry: false,
  });
}

/**
 * Hromadný import identifikátorů. Aditivní a idempotentní — nové pozice přidá
 * jako `ingest=true`, u existujících nechá příznaky být. Vrací celý registr.
 */
export function useImportPositions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (identifiers: string[]) =>
      api.put<PositionRegistry>('/etl/positions/import', { identifiers }),
    onSuccess: (registry) => qc.setQueryData(queryKeys.positions, registry),
  });
}

/** Změna příznaků/labelu jedné pozice. Posílá jen změněná pole. */
export function useUpdatePosition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ placement_id, ...body }: PositionPatchRequest & { placement_id: string }) =>
      api.patch<PositionConfig>(`/etl/positions/${encodeURIComponent(placement_id)}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.positions }),
  });
}

export function useDeletePosition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (placement_id: string) =>
      api.delete(`/etl/positions/${encodeURIComponent(placement_id)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.positions }),
  });
}

// ---------------------------------------------------------------------------
// 02 — Overview
// ---------------------------------------------------------------------------

export function useOverview(filters: Partial<GlobalFilters>, enabled = true) {
  return useQuery<OverviewResponse>({
    queryKey: queryKeys.overview(filters),
    queryFn: () =>
      api.get<OverviewResponse>('/overview/positions', { params: filtersToParams(filters) }),
    enabled,
    staleTime: 60_000,
  });
}

/**
 * Katalog placementů pro vyhledávací multiselect (02 FR-1).
 *
 * Vrací VŽDY plnou sadu placementů za období — nezávisle na tom, co je zrovna
 * vybráno. Do query klíče proto z filtrů jde jen období, ne výběr placementů;
 * jinak by se nabídka měnila podle vlastního výběru (ta stará kruhová chyba).
 */
export function usePlacementCatalog(filters: Partial<GlobalFilters>, enabled = true) {
  const period = { date_from: filters.date_from, date_to: filters.date_to };
  return useQuery<PlacementCatalog>({
    queryKey: ['overview', 'placements', period],
    queryFn: () => api.get<PlacementCatalog>('/overview/placements', { params: period }),
    enabled,
    staleTime: 60_000,
  });
}

/**
 * Nabídka hodnot pro dropdowny (web / zařízení / pozice) — používá je guard.
 *
 * Dřív se odvozovala z řádků tabulky overview, což bylo kruhové: filtr na web A
 * schoval web B z nabídky. Teď čte z katalogu placementů, který vrací plnou
 * sadu za období bez ohledu na výběr — kruhová chyba tím zmizela. Overview
 * samo tuhle nabídku už nepoužívá (přešlo na `<PlacementMultiSelect>`).
 */
export function useFilterOptions(filters: Partial<GlobalFilters>, enabled = true) {
  const catalog = usePlacementCatalog(filters, enabled);

  const placements = catalog.data?.placements ?? [];
  const distinct = (pick: (p: PlacementCatalog['placements'][number]) => string) =>
    [...new Set(placements.map(pick).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'cs'));

  return {
    isLoading: catalog.isLoading,
    domains: distinct((p) => p.domain),
    types: distinct((p) => p.type),
    positions: distinct((p) => p.position),
  };
}

function positionTab<T>(id: string, tab: string, filters: Partial<GlobalFilters>, extra = {}) {
  return api.get<T>(`/overview/position/${encodeURIComponent(id)}/${tab}`, {
    params: { ...filtersToParams(filters), ...extra },
  });
}

export function usePositionSummary(
  id: string,
  filters: Partial<GlobalFilters>,
  granularity = 'day',
) {
  return useQuery<PositionSummary>({
    queryKey: queryKeys.positionTab(id, `summary:${granularity}`, filters),
    queryFn: () => positionTab<PositionSummary>(id, 'summary', filters, { granularity }),
    enabled: Boolean(id),
  });
}

export function usePositionVariants(id: string, filters: Partial<GlobalFilters>) {
  return useQuery<PositionVariants>({
    queryKey: queryKeys.positionTab(id, 'variants', filters),
    queryFn: () => positionTab<PositionVariants>(id, 'variants', filters),
    enabled: Boolean(id),
  });
}

export function usePositionTime(id: string, filters: Partial<GlobalFilters>) {
  return useQuery<PositionTime>({
    queryKey: queryKeys.positionTab(id, 'time', filters),
    queryFn: () => positionTab<PositionTime>(id, 'time', filters),
    enabled: Boolean(id),
  });
}

export function usePositionGeo(id: string, filters: Partial<GlobalFilters>) {
  return useQuery<PositionGeo>({
    queryKey: queryKeys.positionTab(id, 'geo', filters),
    queryFn: () => positionTab<PositionGeo>(id, 'geo', filters),
    enabled: Boolean(id),
  });
}

export function usePositionDevice(id: string, filters: Partial<GlobalFilters>) {
  return useQuery<PositionDevice>({
    queryKey: queryKeys.positionTab(id, 'device', filters),
    queryFn: () => positionTab<PositionDevice>(id, 'device', filters),
    enabled: Boolean(id),
  });
}

export function usePositionGeometry(
  id: string,
  filters: Partial<GlobalFilters>,
  includeArtifacts: boolean,
) {
  return useQuery<PositionGeometry>({
    queryKey: queryKeys.positionTab(id, `geometry:${includeArtifacts}`, filters),
    queryFn: () =>
      positionTab<PositionGeometry>(id, 'geometry', filters, {
        include_artifacts: includeArtifacts,
      }),
    enabled: Boolean(id),
  });
}

export function usePositionEconomy(
  id: string,
  filters: Partial<GlobalFilters>,
  granularity = 'day',
) {
  return useQuery<PositionEconomy>({
    queryKey: queryKeys.positionTab(id, `economy:${granularity}`, filters),
    queryFn: () => positionTab<PositionEconomy>(id, 'economy', filters, { granularity }),
    enabled: Boolean(id),
  });
}

export function useCompare(
  placementIds: string[],
  filters: Partial<GlobalFilters>,
  baseline: string | undefined,
  granularity = 'day',
) {
  return useQuery<Compare>({
    queryKey: ['compare', [...placementIds].sort(), baseline ?? null, granularity, filters],
    queryFn: () =>
      api.get<Compare>('/overview/compare', {
        params: { ...filtersToParams(filters), pid: placementIds, baseline, granularity },
      }),
    enabled: placementIds.length >= 2,
  });
}

/** Zařízení & prohlížeče globálně napříč všemi placementy s debug logy. */
export function useDevicesOverview(filters: Partial<GlobalFilters>, r2b2Scope = 'all') {
  return useQuery<DevicesOverview>({
    queryKey: ['devices-overview', filters, r2b2Scope],
    queryFn: () =>
      api.get<DevicesOverview>('/overview/devices', {
        params: { ...filtersToParams(filters), r2b2_scope: r2b2Scope },
      }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

/** Mapa kliků: filtry stránky navíc k globálním (OS a prostředí z debug logů). */
export interface ClickDeviceFilters {
  ua_os: string[];
  env: string[];
}

function clickParams(filters: Partial<GlobalFilters>, device: ClickDeviceFilters) {
  return {
    date_from: filters.date_from,
    date_to: filters.date_to,
    supplier: filters.supplier?.length ? filters.supplier : undefined,
    phase: filters.phase?.length ? filters.phase : undefined,
    placement_ids: filters.placement_ids?.length ? filters.placement_ids : undefined,
    ua_os: device.ua_os.length ? device.ua_os : undefined,
    env: device.env.length ? device.env : undefined,
  };
}

export function useClickCatalog() {
  return useQuery<ClickCatalog>({
    queryKey: ['clicks', 'catalog'],
    queryFn: () => api.get<ClickCatalog>('/clicks/catalog'),
    staleTime: Infinity,
  });
}

export function useClickOverview(filters: Partial<GlobalFilters>, device: ClickDeviceFilters) {
  return useQuery<ClickOverview>({
    queryKey: ['clicks', 'overview', filters, device],
    queryFn: () =>
      api.get<ClickOverview>('/clicks/overview', { params: clickParams(filters, device) }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

export function useClickHeatmap(
  filters: Partial<GlobalFilters>,
  device: ClickDeviceFilters,
  compare: string,
  periodB?: { date_from: string; date_to: string } | null,
) {
  return useQuery<ClickHeatmap>({
    queryKey: ['clicks', 'heatmap', filters, device, compare, periodB ?? null],
    queryFn: () =>
      api.get<ClickHeatmap>('/clicks/heatmap', {
        params: {
          ...clickParams(filters, device),
          compare,
          date_from_b: compare === 'period' ? periodB?.date_from : undefined,
          date_to_b: compare === 'period' ? periodB?.date_to : undefined,
        },
      }),
    enabled: compare !== 'period' || Boolean(periodB?.date_from && periodB?.date_to),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

export function usePositionChannels(
  id: string,
  filters: Partial<GlobalFilters>,
  granularity = 'day',
) {
  return useQuery<PositionChannels>({
    queryKey: queryKeys.positionTab(id, `channels:${granularity}`, filters),
    queryFn: () => positionTab<PositionChannels>(id, 'channels', filters, { granularity }),
    enabled: Boolean(id),
  });
}

/** Styly kreativ — nativka vs banner Seznamu a šablony (fct_style_hourly). */
export function usePositionStyles(
  id: string,
  filters: Partial<GlobalFilters>,
  granularity = 'day',
) {
  return useQuery<PositionStyles>({
    queryKey: queryKeys.positionTab(id, `styles:${granularity}`, filters),
    queryFn: () => positionTab<PositionStyles>(id, 'styles', filters, { granularity }),
    enabled: Boolean(id),
  });
}

/** Pořadí reklamy na stránce — reloadCount (fct_imp_order_hourly). */
export function usePositionImpOrder(
  id: string,
  filters: Partial<GlobalFilters>,
  opts: { granularity?: string; scope?: string; channel?: string },
) {
  const { granularity = 'day', scope = 'page', channel = 'all' } = opts;
  return useQuery<PositionImpOrder>({
    queryKey: queryKeys.positionTab(id, `imp-order:${granularity}:${scope}:${channel}`, filters),
    queryFn: () =>
      positionTab<PositionImpOrder>(id, 'imp-order', filters, { granularity, scope, channel }),
    enabled: Boolean(id),
    placeholderData: keepPreviousData,
  });
}

export function usePositionQuality(id: string, filters: Partial<GlobalFilters>) {
  return useQuery<PositionQuality>({
    queryKey: queryKeys.positionTab(id, 'quality', filters),
    queryFn: () => positionTab<PositionQuality>(id, 'quality', filters),
    enabled: Boolean(id),
  });
}

/** Obecný metrics dotaz (01 FR-5) — pro ad-hoc grafy. */
export function useMetricsQuery(body: MetricsQueryRequest, enabled = true) {
  return useQuery<MetricsQueryResponse>({
    queryKey: ['metrics-query', body],
    queryFn: () => api.post<MetricsQueryResponse>('/metrics/query', body),
    enabled,
  });
}

// ---------------------------------------------------------------------------
// 03 — Experimenty (BEZ Trella — zakládají se ručně)
// ---------------------------------------------------------------------------

export function useExperiments(state?: string) {
  return useQuery<ExperimentListItem[]>({
    queryKey: queryKeys.experiments(state),
    queryFn: () => api.get<ExperimentListItem[]>('/experiments', { params: { state } }),
  });
}

export function useExperiment(id: number) {
  return useQuery<Experiment>({
    queryKey: queryKeys.experiment(id),
    queryFn: () => api.get<Experiment>(`/experiments/${id}`),
    enabled: Number.isFinite(id),
  });
}

export function useExperimentEvaluation(id: number) {
  return useQuery<ExperimentEvaluation>({
    queryKey: queryKeys.experimentEvaluation(id),
    queryFn: () => api.get<ExperimentEvaluation>(`/experiments/${id}/evaluation`),
    enabled: Number.isFinite(id),
    retry: false,
  });
}

export function useExperimentEnvironment(id: number, enabled = true) {
  return useQuery<ExperimentEnvironment>({
    queryKey: queryKeys.experimentEnvironment(id),
    queryFn: () => api.get<ExperimentEnvironment>(`/experiments/${id}/environment`),
    enabled: enabled && Number.isFinite(id),
    retry: false,
  });
}

export function useExperimentMapping(id: number) {
  return useQuery<MappingReview>({
    queryKey: queryKeys.experimentMapping(id),
    queryFn: () => api.get<MappingReview>(`/experiments/${id}/mapping`),
    enabled: Number.isFinite(id),
  });
}

export function useExperimentReport(id: number) {
  return useQuery<{ markdown?: string; model?: string; created_at?: string }>({
    queryKey: queryKeys.experimentReport(id),
    queryFn: () => api.get(`/experiments/${id}/report`),
    enabled: Number.isFinite(id),
    retry: false,
  });
}

export function useExperimentDecisions(id: number) {
  return useQuery<ExperimentDecision[]>({
    queryKey: queryKeys.experimentDecisions(id),
    queryFn: () => api.get<ExperimentDecision[]>(`/experiments/${id}/decisions`),
    enabled: Number.isFinite(id),
  });
}

export function useCreateExperiment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ExperimentCreate) => api.post<Experiment>('/experiments', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['experiments'] }),
  });
}

/** Definice kanálu na placementu s podílem trafficu — výběr defaultu a testu (DK split). */
export function usePlacementChannels(placementId: string, dateFrom?: string, dateTo?: string) {
  return useQuery<PlacementChannels>({
    queryKey: ['experiments', 'placement-channels', placementId, dateFrom, dateTo],
    queryFn: () =>
      api.get<PlacementChannels>('/experiments/placement-channels', {
        params: { placement_id: placementId, date_from: dateFrom, date_to: dateTo },
      }),
    enabled: placementId.trim().length > 3,
    retry: false,
  });
}

export function useCreateDkSplit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DkSplitCreate) => api.post<Experiment>('/experiments/dk-split', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['experiments'] }),
  });
}

/** Spustí návrh DK splitu (např. od AI přes MCP): potvrdí mapping, stav → running. */
export function useActivateExperiment(experimentId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<Experiment>(`/experiments/${experimentId}/activate`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.experiment(experimentId) });
      void qc.invalidateQueries({ queryKey: queryKeys.experimentMapping(experimentId) });
      void qc.invalidateQueries({ queryKey: queryKeys.experimentEvaluation(experimentId) });
      void qc.invalidateQueries({ queryKey: ['experiments'] });
    },
  });
}

export function useConfirmMapping(experimentId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) =>
      api.post(`/experiments/${experimentId}/mapping/confirm`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.experiment(experimentId) });
      void qc.invalidateQueries({ queryKey: queryKeys.experimentMapping(experimentId) });
      void qc.invalidateQueries({ queryKey: queryKeys.experimentEvaluation(experimentId) });
    },
  });
}

export function useEvaluateExperiment(experimentId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/experiments/${experimentId}/evaluate`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: queryKeys.experimentEvaluation(experimentId) }),
  });
}

export function useAddDecision(experimentId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { decision: string; rationale_cs: string }) =>
      api.post<ExperimentDecision>(`/experiments/${experimentId}/decisions`, body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: queryKeys.experimentDecisions(experimentId) }),
  });
}

// ---------------------------------------------------------------------------
// 04 — Guard
// ---------------------------------------------------------------------------

// Guard vrstva 1 (r2b2 vs PX z fct_position_hourly, scoped na flag `guard`).
function guardBasic<T>(path: string, filters: Partial<GlobalFilters>) {
  return api.get<T>(`/guard/${path}`, { params: filtersToParams(filters) });
}

export function useGuardPlacements(filters: Partial<GlobalFilters>) {
  return useQuery<GuardPlacements>({
    queryKey: ['guard', 'placements', filters],
    queryFn: () => guardBasic<GuardPlacements>('placements', filters),
  });
}

export function useGuardPlacementDetail(
  placementId: string,
  filters: Partial<GlobalFilters>,
  granularity = 'day',
) {
  return useQuery<GuardPlacementDetail>({
    queryKey: ['guard', 'placement', placementId, filters, granularity],
    queryFn: () =>
      api.get<GuardPlacementDetail>(`/guard/placement/${encodeURIComponent(placementId)}`, {
        params: { ...filtersToParams(filters), granularity },
      }),
    enabled: Boolean(placementId),
    placeholderData: keepPreviousData,
  });
}

// Guard — konkurenční pozice vs r2b2 (surové CTR, kanály, čas, verdikt).
export function useGuardCompare(
  placementId: string,
  filters: Partial<GlobalFilters>,
  opts: { granularity?: string; commonHours?: boolean; against?: string },
) {
  const { granularity = 'day', commonHours = true, against } = opts;
  return useQuery<GuardCompare>({
    queryKey: ['guard', 'placement', placementId, 'compare', filters, granularity, commonHours, against],
    queryFn: () =>
      api.get<GuardCompare>(`/guard/placement/${encodeURIComponent(placementId)}/compare`, {
        params: {
          ...filtersToParams(filters),
          granularity,
          common_hours: commonHours,
          against: against || undefined,
        },
      }),
    enabled: Boolean(placementId),
    placeholderData: keepPreviousData,
  });
}

// Guard — banner vs nativka Seznamu, konkurence vs r2b2 (stejné párování jako compare).
export function useGuardStyles(
  placementId: string,
  filters: Partial<GlobalFilters>,
  opts: { granularity?: string; commonHours?: boolean; against?: string },
) {
  const { granularity = 'day', commonHours = true, against } = opts;
  return useQuery<GuardStyles>({
    queryKey: ['guard', 'placement', placementId, 'styles', filters, granularity, commonHours, against],
    queryFn: () =>
      api.get<GuardStyles>(`/guard/placement/${encodeURIComponent(placementId)}/styles`, {
        params: {
          ...filtersToParams(filters),
          granularity,
          common_hours: commonHours,
          against: against || undefined,
        },
      }),
    enabled: Boolean(placementId),
    placeholderData: keepPreviousData,
  });
}

// Guard — pořadí reklamy na stránce (reloadCount) PX vs r2b2.
export function useGuardImpOrder(
  placementId: string,
  filters: Partial<GlobalFilters>,
  opts: {
    granularity?: string;
    commonHours?: boolean;
    against?: string;
    scope?: string;
    channel?: string;
  },
) {
  const { granularity = 'day', commonHours = true, against, scope = 'page', channel = 'all' } =
    opts;
  return useQuery<GuardImpOrder>({
    queryKey: [
      'guard', 'placement', placementId, 'imp-order', filters, granularity, commonHours,
      against, scope, channel,
    ],
    queryFn: () =>
      api.get<GuardImpOrder>(`/guard/placement/${encodeURIComponent(placementId)}/imp-order`, {
        params: {
          ...filtersToParams(filters),
          granularity,
          common_hours: commonHours,
          against: against || undefined,
          scope,
          channel,
        },
      }),
    enabled: Boolean(placementId),
    placeholderData: keepPreviousData,
  });
}

// Guard — definice kanálů PX vs r2b2 + uložená mapování (skupiny) a návrhy.
export function useGuardChannelDefs(
  placementId: string,
  filters: Partial<GlobalFilters>,
  opts: { commonHours?: boolean; against?: string },
) {
  const { commonHours = true, against } = opts;
  return useQuery<GuardChannelDefs>({
    queryKey: ['guard', 'placement', placementId, 'channel-defs', filters, commonHours, against],
    queryFn: () =>
      api.get<GuardChannelDefs>(
        `/guard/placement/${encodeURIComponent(placementId)}/channel-defs`,
        { params: { ...filtersToParams(filters), common_hours: commonHours, against: against || undefined } },
      ),
    enabled: Boolean(placementId),
    placeholderData: keepPreviousData,
  });
}

export function useSaveChannelMap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      label: string;
      competitor_channels: string[];
      r2b2_channels: string[];
      placement_id?: string | null;
    }) => api.post('/guard/channel-maps', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['guard'] }),
  });
}

export function useUpdateChannelMap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: number;
      label?: string;
      competitor_channels?: string[];
      r2b2_channels?: string[];
    }) => api.patch(`/guard/channel-maps/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['guard'] }),
  });
}

export function useDeleteChannelMap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/guard/channel-maps/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['guard'] }),
  });
}

// Guard vrstva 2 — closer varianty podle kraje (enriched load logy, chi² test).
export function useGuardRegionVariants(placementId: string, filters: Partial<GlobalFilters>) {
  return useQuery<GuardRegionVariants>({
    queryKey: ['guard', 'placement', placementId, 'regions', filters],
    queryFn: () =>
      api.get<GuardRegionVariants>(
        `/guard/placement/${encodeURIComponent(placementId)}/regions`,
        { params: filtersToParams(filters) },
      ),
    enabled: Boolean(placementId),
  });
}

// Guard vrstva 2 — forenzní detail kohorty webu×dodavatel×fáze (enriched).
export function useGuardFraud(
  web: string,
  supplier: string,
  phase: string | undefined,
  filters: Partial<GlobalFilters>,
) {
  return useQuery<FraudAssessment>({
    queryKey: ['guard', web, 'fraud', supplier, phase ?? null, filters],
    queryFn: () =>
      api.get<FraudAssessment>(`/guard/${encodeURIComponent(web)}/fraud`, {
        params: { ...filtersToParams(filters), supplier, phase },
      }),
    enabled: Boolean(web && supplier),
    retry: false,
  });
}

export function useGuardPhases(
  web: string,
  supplier: string,
  phase: string | undefined,
  filters: Partial<GlobalFilters>,
) {
  return useQuery<GuardPhases>({
    queryKey: ['guard', web, 'phases', supplier, phase ?? null, filters],
    queryFn: () =>
      api.get<GuardPhases>(`/guard/${encodeURIComponent(web)}/phases`, {
        params: { ...filtersToParams(filters), supplier, phase },
      }),
    enabled: Boolean(web),
    retry: false,
  });
}

export function useGuardRisks(
  web: string,
  supplier: string,
  phase: string | undefined,
  filters: Partial<GlobalFilters>,
) {
  return useQuery<GuardRiskReport>({
    queryKey: ['guard', web, 'risks', supplier, phase ?? null, filters],
    queryFn: () =>
      api.get<GuardRiskReport>(`/guard/${encodeURIComponent(web)}/environment/risks`, {
        params: { ...filtersToParams(filters), supplier, phase },
      }),
    enabled: Boolean(web),
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// 06 — Znalostní báze
// ---------------------------------------------------------------------------

export function useKnowledgeSearch(query: string, filters?: Record<string, string | undefined>) {
  return useQuery<KnowledgeSearchResult>({
    queryKey: queryKeys.knowledgeSearch(query, filters),
    queryFn: () =>
      api.get<KnowledgeSearchResult>('/knowledge/search', { params: { q: query, ...filters } }),
    enabled: query.trim().length > 1,
  });
}

export function useInsights(filters?: Record<string, string | undefined>) {
  return useQuery<Insight[]>({
    queryKey: queryKeys.insights(filters),
    // Endpoint je stránkovaný; UI zatím zobrazuje první stránku.
    queryFn: async () =>
      (await api.get<InsightList>('/knowledge/insights', { params: filters })).items,
  });
}

export function useInsight(slug: string) {
  return useQuery<Insight>({
    queryKey: queryKeys.insight(slug),
    queryFn: () => api.get<Insight>(`/knowledge/insights/${encodeURIComponent(slug)}`),
    enabled: Boolean(slug),
  });
}

/** Kontextový panel „Související poznatky" na detailu placementu/experimentu. */
export function useRelatedNotes(subject: string, enabled = true) {
  return useQuery<Insight[]>({
    queryKey: queryKeys.relatedNotes(subject),
    queryFn: () => api.get<Insight[]>('/knowledge/related', { params: { q: subject } }),
    enabled: enabled && Boolean(subject),
    retry: false,
  });
}

export function useSetInsightStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { slug: string; status: string; note?: string; superseded_by?: string }) =>
      api.post(`/knowledge/insights/${encodeURIComponent(body.slug)}/status`, {
        status: body.status,
        note: body.note,
        superseded_by: body.superseded_by,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['knowledge'] }),
  });
}

/** Lidská revize AI draftu / návrhu změny (analyst) — schválit nebo zamítnout. */
export function useReviewInsight() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { slug: string; state: 'approved' | 'rejected'; note?: string }) =>
      api.post(`/knowledge/insights/${encodeURIComponent(body.slug)}/review`, {
        state: body.state,
        note: body.note ?? '',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['knowledge'] }),
  });
}

/** „Uložit jako poznatek" z chatu (07 FR-1 → add_insight, provenance `chat`). */
export function useCreateInsight() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      title: string;
      summary: string;
      body_md: string;
      tags: string[];
      confidence: string;
    }) =>
      api.post<WriteResult>('/knowledge/insights', {
        ...body,
        provenance: 'chat',
        source_module: 'chat',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['knowledge'] }),
  });
}

/**
 * Obecné založení poznatku z UI znalostní báze (06 FR-4).
 *
 * Na rozdíl od `useCreateInsight` posílá plný `InsightCreate` a NEnastavuje
 * provenanci — tuhle cestu backend značí jako `human`. Vrací `WriteResult`,
 * který nese near-duplikáty (`similar`, `duplicate_of`), aby UI mohlo místo
 * tichého duplikátu nabídnout existující poznatek.
 */
export function useAddInsight() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InsightCreate) => api.post<WriteResult>('/knowledge/insights', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['knowledge'] }),
  });
}

// ---------------------------------------------------------------------------
// 07 — AI vrstva
// ---------------------------------------------------------------------------

/** Konfigurace viditelná všem (vypínač chatu, nabídka modelů). */
export function useAiConfig() {
  return useQuery<AiConfig>({
    queryKey: queryKeys.aiConfig,
    queryFn: () => api.get<AiConfig>('/ai/config'),
    staleTime: 30_000,
  });
}

/** Plná konfigurace včetně jobů a rozpočtu — jen pro adminy. */
export function useAiConfigAdmin(enabled = true) {
  return useQuery<AiConfigAdmin>({
    queryKey: queryKeys.aiConfigAdmin,
    queryFn: () => api.get<AiConfigAdmin>('/ai/config/admin'),
    enabled,
    retry: false,
  });
}

export function useUpdateAiConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AiConfigUpdate) => api.put<AiConfigAdmin>('/ai/config', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.aiConfig });
      void qc.invalidateQueries({ queryKey: queryKeys.aiConfigAdmin });
    },
  });
}

export function useAiRuns(filters?: Record<string, string | undefined>) {
  return useQuery<AiRun[]>({
    queryKey: queryKeys.aiRuns(filters),
    queryFn: async () => (await api.get<AiRunList>('/ai/runs', { params: filters })).items,
  });
}

export function useAiJobs() {
  return useQuery<AiJob[]>({
    queryKey: queryKeys.aiJobs,
    queryFn: () => api.get<AiJob[]>('/ai/jobs'),
  });
}

export function useAiBudget() {
  return useQuery<AiBudget>({
    queryKey: queryKeys.aiBudget,
    queryFn: () => api.get<AiBudget>('/ai/budget'),
  });
}

export function useRerunAiJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: number) => api.post(`/ai/runs/${runId}/rerun`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai', 'runs'] }),
  });
}

export function useYieldTips() {
  return useQuery<YieldTip[]>({
    queryKey: queryKeys.yieldTips,
    queryFn: () => api.get<YieldTip[]>('/ai/tips'),
  });
}

export function useUpdateYieldTip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { id: number; state: string; feedback?: string }) =>
      api.post<YieldTip>(`/ai/tips/${body.id}/state`, {
        state: body.state,
        feedback: body.feedback ?? '',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.yieldTips }),
  });
}

// --- chat ---

export function useChatSessions() {
  return useQuery<ChatSession[]>({
    queryKey: queryKeys.chatSessions,
    queryFn: () => api.get<ChatSession[]>('/ai/chat/sessions'),
  });
}

export function useCreateChatSession() {
  const qc = useQueryClient();
  return useMutation({
    // `page_context` je v OpenAPI `type: object` bez vlastností, což
    // openapi-typescript vyrenderuje jako neplnitelný Record<string, never>.
    // Cast je proto tady, na jednom místě u hranice API — ne v každé komponentě.
    mutationFn: (body: { title?: string; model?: string; page_context?: PageContext }) =>
      api.post<ChatSession>('/ai/chat/sessions', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.chatSessions }),
  });
}

export function useChatMessages(sessionId: number | null) {
  return useQuery<ChatMessage[]>({
    queryKey: queryKeys.chatMessages(sessionId ?? 0),
    queryFn: () => api.get<ChatMessage[]>(`/ai/chat/sessions/${sessionId}/messages`),
    enabled: sessionId !== null,
  });
}


// ---------------------------------------------------------------------------
// Přístupy k MCP — statické tokeny a OAuth klienti (jen admin)
// ---------------------------------------------------------------------------

export interface McpTokenRow {
  id: number;
  name: string;
  scopes: string;
  created_at: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
}

/** Hodnota tokenu přijde jen v odpovědi na vytvoření — nikde jinde už ne. */
export interface McpTokenCreated extends McpTokenRow {
  token: string;
}

export interface OAuthClientRow {
  client_id: string;
  client_name: string;
  redirect_uris: string[];
  scope: string;
  dynamic: boolean;
  is_active: boolean;
  created_at: string | null;
  active_tokens: number;
  last_used_at: string | null;
  users: string[];
}

export function useMcpTokens() {
  return useQuery<McpTokenRow[]>({
    queryKey: queryKeys.mcpTokens,
    queryFn: () => api.get<McpTokenRow[]>('/admin/mcp-tokens'),
    retry: false,
  });
}

export function useCreateMcpToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; scopes: string }) =>
      api.post<McpTokenCreated>('/admin/mcp-tokens', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.mcpTokens }),
  });
}

export function useRevokeMcpToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ message_cs: string }>(`/admin/mcp-tokens/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.mcpTokens }),
  });
}

export function useOAuthClients() {
  return useQuery<OAuthClientRow[]>({
    queryKey: queryKeys.oauthClients,
    queryFn: () => api.get<OAuthClientRow[]>('/admin/oauth-clients'),
    retry: false,
  });
}

export function useRevokeOAuthClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (clientId: string) =>
      api.delete<{ message_cs: string }>(`/admin/oauth-clients/${clientId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.oauthClients }),
  });
}
